import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ISortState } from './table-endereco-entidade.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { ModalController } from '@ionic/angular';
import { MatDialog } from '@angular/material/dialog';
import { ModalEnderecoEntidadeComponent } from '../modal-endereco-entidade/modal-endereco-entidade.component';
import { ModalComponent } from '../../modal/modal.component';
import { ModalHistoricComponent } from 'src/app/modules/shared/modals/modal-historic/modal-historic.component';

@Component({
  selector: 'app-table-endereco-entidade',
  templateUrl: './table-endereco-entidade.component.html',
  styleUrls: ['./table-endereco-entidade.component.scss'],
})
export class TableEnderecoEntidadeComponent {
  @Input() allData: any[] = [];
  @Input() isMaster: boolean = false;
  @Output() allDataChange = new EventEmitter<any>();
  @Input() inclusaoDisabled: boolean = false;
  @Input() exclusaoDisabled: boolean = false;
  @Input() edicaoDisabled: boolean = false;
  disabledTooltipMessage: string = 'Ação não permitida';
  sortState: ISortState = {
    sortProperty: 'cep',
    sortDirection: 'asc',
  };
  pageSize: number = 10;
  pageIndex: number = 0;
  totalPages: number = 1;
  totalElements: number = 0;
  displayedColumns: string[] = [
    'tipo',
    'cep',
    'endereco',
    'bairro',
    'cidade',
    'estado',
    'numero',
    'actions',
  ];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private toastService: ToastService,
    private modalCtrl: ModalController,
    public dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allData']) {
      this.allData = this.allData;
      this.dataSource.data = this.allData;
    }
  }

  async openNewEnderecoDialog(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalEnderecoEntidadeComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { isEditMode: false },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.allData.push(data);
      this.dataSource.data = this.allData;
      this.allDataChange.emit(this.allData);
    }
  }

  async openEditEnderecoDialog(endereco: any): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalEnderecoEntidadeComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { data: endereco, isEditMode: true },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.onUpdate(data);
    }
  }

  openDeleteDialog(id: string): void {
    const modalTitle: string = 'Excluir';
    const modalContent: string =
      'Você tem certeza que deseja excluir este endereço?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onDelete.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openInactivateDialog(id: any): void {
    const modalTitle: string = 'Inativar';
    const modalContent: string =
      'Você tem certeza que deseja inativar este endereço?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onInactivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openActivateDialog(id: any): void {
    const modalTitle: string = 'Ativar';
    const modalContent: string =
      'Você tem certeza que deseja ativar este endereço?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onActivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  onInactivateModalConfirm(id: string): void {
    let enderecoObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    enderecoObj.status = false;

    this.onUpdate(enderecoObj);
    this.toastService.showSuccess('Endereço ativado com sucesso');
  }

  onActivateModalConfirm(id: string): void {
    let enderecoObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    enderecoObj.status = true;

    this.onUpdate(enderecoObj);
    this.toastService.showSuccess('Endereço ativado com sucesso');
  }

  onDelete(id: string) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== id : oldData.tempId !== id
    );
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.toastService.showSuccess('Endereço removido com sucesso');
    this.allDataChange.emit(this.allData);
  }

  onUpdate(data: any) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== data.id : oldData.tempId !== data.tempId
    );
    newDataArray.push(data);
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.allDataChange.emit(this.allData);
  }

  onSortChange(sortProperty: ISortState['sortProperty']) {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection =
        this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }

    const sortedData = this.dataSource.data.sort((a, b) => {
      const aValue = this.getMappedValue(a, sortProperty);
      const bValue = this.getMappedValue(b, sortProperty);

      let comparison = 0;
      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      return this.sortState.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.dataSource.data = sortedData;
  }


  // Map the sortProperty to nested values
  getMappedValue(obj: any, sortProperty: string) {
    switch (sortProperty) {
      case 'cidade':
        return obj.cidade?.nome || ''; // Sort by cidade.nome
      case 'estado':
        return obj.cidade?.estado?.nome || ''; // Sort by cidade.estado.nome
      default:
        return obj[sortProperty]; // Sort by default properties like 'cep', 'bairro', etc.
    }
  }

  onSortChangeKeydown(
    event: KeyboardEvent,
    sortProperty: ISortState['sortProperty']
  ) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.onSortChange(sortProperty);
    }
  }

  async openDialogHistoricoEndereco(adressId:any){
  const data = {
      type: 'enderecoEntidade',
      adressId: adressId,
      fieldMappings: {
        cep: 'CEP',
        rua: 'Endereço',
        bairro: 'Bairro',
        cidade: 'Cidade',
        estado: 'Estado',
        numero: 'Número',
        status:'Status'
      }
    }
    const modal = await this.modalCtrl.create({
      component: ModalHistoricComponent,
      componentProps: {
        data: data,
        fieldMappings: data.fieldMappings,
      },
    });

    modal.present();
  }

  
}
